import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import ProductForm from '../components/ProductForm'
import ConfirmDialog from '../components/ConfirmDialog'
import Modal from '../components/common/Modal'
import { SkeletonTable } from '../components/SkeletonLoader'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import { api } from '../lib/api'
import { formatPrice } from '../utils/formatPrice'
import { extractErrorMessage } from '../utils/errorHandler'

export default function ProductsPage() {
  const navigate = useNavigate()
  const filterManager = useAdminFilters({ defaultLimit: 10 })
  const { search, debouncedSearch, page, limit, queryParams, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  const [products, setProducts] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingProduct, setEditingProduct] = useState<any>(null)
  const [pagination, setPagination] = useState({ total: 0, pages: 0, page: 1, limit: 10 })
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; productId: string | null; isLoading: boolean }>({
    isOpen: false,
    productId: null,
    isLoading: false,
  })

  useEffect(() => {
    fetchData()
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [productsRes, categoriesRes] = await Promise.all([
        api.getProducts(page, limit, debouncedSearch, filterManager.filters),
        categories.length === 0 ? api.getCategories(1, 100) : Promise.resolve({ data: { data: categories } }),
      ])
      setProducts(productsRes.data.data || [])
      if (productsRes.data?.pagination) {
        setPagination(productsRes.data.pagination)
      }
      if (categoriesRes.data?.data) {
        setCategories(categoriesRes.data.data)
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage)
      console.error('Fetch products error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddClick = () => {
    setEditingId(null)
    setEditingProduct(null)
    setShowModal(true)
  }

  const handleEditClick = async (product: any) => {
    try {
      const res = await api.getProduct(product.id)
      setEditingId(product.id)
      setEditingProduct(res.data.data)
      setShowModal(true)
    } catch (error) {
      toast.error('Failed to load product details')
    }
  }

  const handleSubmit = async (formData: FormData) => {
    try {
      if (editingId) {
        await api.updateProduct(editingId, formData)
        toast.success('Product updated successfully')
      } else {
        await api.createProduct(formData)
        toast.success('Product created successfully')
      }

      setShowModal(false)
      fetchData()
    } catch (error: any) {
      let errorMessage = 'Failed to save product'

      // Handle various error formats from backend
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message

        // If there are validation errors, format them
        if (error.response?.data?.errors && typeof error.response.data.errors === 'object') {
          const errors = error.response.data.errors
          const errorList = Object.entries(errors)
            .map(([, msg]) => `• ${msg}`)
            .join('\n')
          toast.error(`${errorMessage}\n${errorList}`)
          console.error('Submit error:', error)
          return
        }
      }

      toast.error(errorMessage)
      console.error('Submit error:', error)
    }
  }

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ isOpen: true, productId: id, isLoading: false })
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.productId) return

    setDeleteConfirm((prev) => ({ ...prev, isLoading: true }))

    try {
      await api.deleteProduct(deleteConfirm.productId)
      toast.success('Product deleted successfully')
      setDeleteConfirm({ isOpen: false, productId: null, isLoading: false })
      fetchData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete product')
      setDeleteConfirm((prev) => ({ ...prev, isLoading: false }))
    }
  }

  const handleCancelDelete = () => {
    setDeleteConfirm({ isOpen: false, productId: null, isLoading: false })
  }


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">Products</h1>
        <button
          onClick={handleAddClick}
          className="flex items-center justify-center sm:justify-start gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] transition w-full sm:w-auto"
        >
          <Plus size={20} />
          <span>Add Product</span>
        </button>
      </div>

      <AdminFilters
        search={search}
        searchPlaceholder="Search products by name or SKU..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={loading}
        fields={[
          {
            key: 'categoryId',
            label: 'Category',
            type: 'select',
            options: categories.map((c: any) => ({ label: c.name, value: c.id })),
          },
          {
            key: 'tag',
            label: 'Tag',
            type: 'select',
            options: [
              { label: 'NEW', value: 'NEW' },
              { label: 'BEST_SELLER', value: 'BEST_SELLER' },
              { label: 'FEATURED', value: 'FEATURED' },
              { label: 'TRENDING', value: 'TRENDING' },
              { label: 'DISCOUNT', value: 'DISCOUNT' },
            ],
          },
        ]}
      />

      {loading ? (
        <SkeletonTable rows={5} cols={6} />
      ) : products.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-[#F6EEE7]">
          <p className="text-gray-600">
            {hasActiveFilters ? 'No products match your active filters.' : 'No products yet. Create one to get started!'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#F6EEE7] shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F6EEE7] border-b-2 border-[#F6EEE7]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F6EEE7]">
              {products.map((product: any) => (
                <tr key={product.id} className="hover:bg-[#F6EEE7]/50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{product.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{product.sku || '-'}</td>
                  <td className="px-6 py-4 text-sm font-semibold whitespace-nowrap">
                    {product.discountPrice && Number(product.discountPrice) < Number(product.price) ? (
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[#6D3A0D] font-bold">{formatPrice(product.discountPrice)}</span>
                        <span className="line-through text-gray-400 text-xs font-normal">{formatPrice(product.price)}</span>
                      </div>
                    ) : (
                      <span className="text-[#6D3A0D]">{formatPrice(product.price)}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {product.stock} {product.stock <= 5 && <span className="text-red-600 text-xs ml-2">Low!</span>}
                  </td>
                  <td className="px-6 py-4 text-sm flex gap-3">
                    <button
                      onClick={() => navigate(`/products/${product.id}`)}
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                      title="View details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => handleEditClick(product)}
                      className="text-[#6D3A0D] hover:text-[#5D2A0D] font-semibold"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(product.id)}
                      className="text-red-600 hover:text-red-700 font-semibold"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="mt-6">
          <AdminPagination
            page={page}
            limit={limit}
            total={pagination.total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={loading}
            showInfo={true}
          />
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Edit Product' : 'Add New Product'}
        maxWidth="3xl"
      >
        <ProductForm
          initialData={editingProduct}
          categories={categories}
          onSubmit={handleSubmit}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={deleteConfirm.isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  )
}
